import { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, collection, addDoc } from 'firebase/firestore';
import { PiX, PiCheckThin } from "react-icons/pi";
import '../webapp.css';

const questions = [
  {
    section: 'Overall Experience',
    items: [
      {
        type: 'scale',
        label: 'How satisfied are you with your experience using Kierep Software?',
        name: 'satisfaction',
        scale: [1, 2, 3, 4, 5],
      },
      {
        type: 'text',
        label: 'What’s the reason for your score?',
        name: 'reason',
      },
    ],
  },
  {
    section: 'Usability & Features',
    items: [
      {
        type: 'yesno',
        label: 'Was there anything frustrating about your experience today?',
        name: 'frustrating',
        followup: {
          type: 'text',
          label: 'If yes, please describe',
          name: 'frustrating_detail',
        },
      },
      {
        type: 'select',
        label: 'Which feature do you use the most?',
        name: 'most_feature',
        options: ['Shift Call ', 'KierepAI', 'Shift List', 'Staff List'],
      },
      {
        type: 'text',
        label: 'What feature do you wish Kierep Software had?',
        name: 'wish_feature',
      },
    ],
  },
  {
    section: 'Value & Outcome',
    items: [
      {
        type: 'scale',
        label: 'How well does Kierep Software help you achieve your goal?',
        name: 'goal_help',
        scale: [1, 2, 3, 4, 5],
      },
      {
        type: 'pmf',
        label: 'If Kierep Software disappeared tomorrow, how would you feel?',
        name: 'pmf',
        options: ['Very disappointed', 'Somewhat disappointed', 'Not disappointed'],
      },
    ],
  },
  {
    section: 'Support & Trust',
    items: [
      {
        type: 'yesno',
        label: 'Did you get the support you needed today?',
        name: 'support',
      },
      {
        type: 'text',
        label: 'Is there anything we could do to make Kierep Software more valuable to you?',
        name: 'more_valuable',
      },
    ],
  },
  {
    section: 'Final Catch-All',
    items: [
      {
        type: 'text',
        label: 'If you could change one thing about Kierep Software, what would it be?',
        name: 'change_one',
      },
      {
        type: 'text',
        label: 'Any other feedback for us?',
        name: 'other_feedback',
      },
    ],
  },
];

function FeedbackModal({ onClose: propOnClose }) {
  const [answers, setAnswers] = useState({});
  const [showFollowup, setShowFollowup] = useState({});
  const [feedbackGiven, setFeedbackGiven] = useState(null); // null=loading, false=form, true=thank you
  const [loading, setLoading] = useState(true);

  const onClose = propOnClose || (() => {});

  useEffect(() => {
    async function checkFeedback() {
      const user = auth.currentUser;
      if (!user) {
        setFeedbackGiven(false); // fallback: always show form if not logged in
        setLoading(false);
        return;
      }
      const userRef = doc(db, 'companyUsers', user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists() && userSnap.data().feedback === true) {
        setFeedbackGiven(true);
      } else {
        setFeedbackGiven(false);
      }
      setLoading(false);
    }
    checkFeedback();
  }, []);

  const handleChange = (name, value) => {
    setAnswers(a => ({ ...a, [name]: value }));
  };

  const handleYesNo = (name, value, followupName) => {
    setAnswers(a => ({ ...a, [name]: value }));
    if (value === 'Yes' && followupName) {
      setShowFollowup(f => ({ ...f, [name]: true }));
    } else {
      setShowFollowup(f => ({ ...f, [name]: false }));
      if (followupName) {
        setAnswers(a => ({ ...a, [followupName]: '' }));
      }
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const user = auth.currentUser;
    if (!user) return;
    // Save feedback to subcollection
    const feedbackRef = collection(db, 'companyUsers', user.uid, 'feedback');
    await addDoc(feedbackRef, {
      ...answers,
      timestamp: new Date(),
    });
    // Set feedback:true in user doc
    const userRef = doc(db, 'companyUsers', user.uid);
    await setDoc(userRef, { feedback: true }, { merge: true });
    setFeedbackGiven(true);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        {loading ? (
          <p>Loading...</p>
        ) : feedbackGiven ? (
          <div className="feedback-thankyou">
            <button className="feedback-close-button" onClick={onClose}>
                  <PiX />
            </button>
            <PiCheckThin className='feedback-picheck'/>
            <p>Your feedback has been received</p>
          </div>
        ) : (
          <>
            <h2>Do you have feedback for us?</h2>
            <p>This will not take much of your time</p>
            <form className="feedback-form" onSubmit={handleSubmit}>
              {questions.map((q, idx) => (
                <div key={q.section} className="feedback-section">
                  <h4>{`${idx + 1}. ${q.section}`}</h4>
                  {q.items.map((item, itemIdx) => {
                    const letter = String.fromCharCode(97 + itemIdx) + '.';
                    return (
                      <div key={item.name} className="feedback-question">
                        <label>{letter} {item.label}</label>

                        {item.type === 'scale' && (
                          <div className="scale-row">
                            {item.scale.map(val => (
                              <label key={val}>
                                <input
                                  type="radio"
                                  name={item.name}
                                  value={val}
                                  checked={answers[item.name] === val}
                                  onChange={() => handleChange(item.name, val)}
                                />
                                {val}
                              </label>
                            ))}
                          </div>
                        )}

                        {item.type === 'text' && (
                          <textarea
                            name={item.name}
                            value={answers[item.name] || ''}
                            onChange={e => handleChange(item.name, e.target.value)}
                          />
                        )}

                        {item.type === 'yesno' && (
                          <>
                            <div className="yesno-row">
                              <label>
                                <input
                                  type="radio"
                                  name={item.name}
                                  value="Yes"
                                  checked={answers[item.name] === 'Yes'}
                                  onChange={() => handleYesNo(item.name, 'Yes', item.followup?.name)}
                                /> Yes
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  name={item.name}
                                  value="No"
                                  checked={answers[item.name] === 'No'}
                                  onChange={() => handleYesNo(item.name, 'No', item.followup?.name)}
                                /> No
                              </label>
                            </div>

                            {/* Follow-up text appears ONLY if Yes */}
                            {item.followup && showFollowup[item.name] && (
                              <div className="followup-textarea">
                                <textarea
                                  name={item.followup.name}
                                  value={answers[item.followup.name] || ''}
                                  onChange={e => handleChange(item.followup.name, e.target.value)}
                                  placeholder={item.followup.label}
                                />
                              </div>
                            )}
                          </>
                        )}

                        {item.type === 'select' && (
                          <select
                            name={item.name}
                            value={answers[item.name] || ''}
                            onChange={e => handleChange(item.name, e.target.value)}
                          >
                            <option value="">Select an option</option>
                            {item.options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        )}

                        {item.type === 'pmf' && (
                          <div className="pmf-row">
                            {item.options.map(opt => (
                              <label key={opt}>
                                <input
                                  type="radio"
                                  name={item.name}
                                  value={opt}
                                  checked={answers[item.name] === opt}
                                  onChange={() => handleChange(item.name, opt)}
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
              <div className="popupbox-actions">
                <button className="popupbox-btn popupbox-btn-confirm">Submit</button>
                <button className="popupbox-btn popupbox-btn-cancel" onClick={onClose}>Cancel</button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default FeedbackModal;